import reactUpdate from 'immutability-helper';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {getTargetSchemaIdForFileLinkContext} from 'notebook/data-tables/link-contexts/LinkContextTargetSchemaGetter';
import {AddSamplesModalType} from 'notebook/data-tables/link-contexts/SharedConstants';
import StandaloneDataTableAddSampleModal from 'notebook/data-tables/StandaloneDataTableAddSampleModal';
import {AddStandaloneSamplesModal} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStandaloneSamplesModal';
import LegacySamplesSourceCollector from 'notebook/sample-propagation/legacy/LegacySampleSourceCollector';
import {SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import {Table} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import * as ModelActions from 'redux-stores/ModelActions';
import {BenchlingDispatch} from 'redux-stores/Store';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import ModalAnchor from 'ui-library/ModalAnchor';
import fillFromWorklist from 'worklists/util/fillFromWorklist';
import {WORKLIST_FILL_LOCATION, WorklistType} from 'worklists/WorklistConstants';

function mapDispatchToProps(dispatch: BenchlingDispatch): {onSelectFile: (file: BioentitySummary) => void} {
  return {
    onSelectFile(file: BioentitySummary): void {
      // file is the JSON of a fileSummary that is surfaced from StandaloneDataTableAddSampleModal's search
      // component, which does not automatically get cached in a store, so we do that now manually.
      dispatch(ModelActions.filesFetchCompleted({[file.id]: file}));
    },
  };
}

interface DataTableFileLinkContextOwnProps {
  children: (args: {
    table: Table;
    sampleIds: Array<string>;
    addMenuItems: Array<LegacyDropdownMenuItem>;
  }) => JSX.Element;
  dataTable: DataTableConstants.DataTable;
  entryId: string;
  onUpdate: (dataTable: DataTableConstants.DataTable) => void;
  note: Note;
}

interface DataTableFileLinkContextStoreProps {
  onSelectFile: (file: BioentitySummary) => void;
}

interface State {
  openModal: AddSamplesModalType;
}

interface DataTableFileLinkContextConnectorProps {
  fileSummariesById: {[bioentityId: string]: BioentitySummary};
}
type DataTableFileLinkContextProps = DataTableFileLinkContextOwnProps &
  DataTableFileLinkContextConnectorProps &
  DataTableFileLinkContextStoreProps;

class DataTableFileLinkContextConnector extends DataConnector<
  DataTableFileLinkContextOwnProps & DataTableFileLinkContextStoreProps,
  DataTableFileLinkContextConnectorProps
> {
  enhanceProps(): DataTableFileLinkContextConnectorProps {
    // We trigger a fetch for the required entity summaries, but no need to pass the values down
    return {
      fileSummariesById: this.getOrFetchMultipleOptionalPartial(
        ManagedModelType.BIOENTITY_SUMMARY,
        DataTableUtil.getFileIds(this.props.dataTable)
      ),
    };
  }
}

/**
 * DataTableFileLinkContext is a wrapper to DataTableEditable to provide data fetching, new row insertion,
 * and conversion to the TableEditable-compatible form, where each row is linked to a chosen file.
 */
class DataTableFileLinkContext extends React.Component<DataTableFileLinkContextProps, State> {
  override state: State = {openModal: AddSamplesModalType.NONE};
  sampleFilter = [SimpleSampleFilters.BIOENTITY];

  private handleAddExternalSampleClick = (): void => {
    this.setState({openModal: AddSamplesModalType.EXTERNAL});
  };

  private handleAddInternalSampleClick = (): void => {
    this.setState({openModal: AddSamplesModalType.INTERNAL});
  };

  private handleCloseModal = (): void => {
    this.setState({openModal: AddSamplesModalType.NONE});
  };

  private handleAddFile = (file: BioentitySummary, numberOfRows: number): void => {
    this.props.onSelectFile(file);
    const {config, rows} = this.props.dataTable;
    this.props.onUpdate(
      reactUpdate(this.props.dataTable, {
        rows: {
          $push: _.range(numberOfRows).map((i) =>
            DataTableUtil.generateDataTableRow(config, rows.length + i, {fileId: file.id})
          ),
        },
      })
    );
    this.handleCloseModal();
  };

  private handleAddSamples = (sampleDetails: Array<SampleDetails>): void => {
    let dataTable = this.props.dataTable;
    for (const sample of sampleDetails) {
      const {bioentityInfo} = sample;
      if (
        assertAndContinue(
          bioentityInfo != null,
          'Trying to insert a sample without bioentityInfo into an entity result table',
          {sample}
        )
      ) {
        dataTable = DataTableUtil.addRowToStandaloneSampleTable(dataTable, {fileId: bioentityInfo!.id});
      }
    }
    this.props.onUpdate(dataTable);
  };

  private handleAddSamplesFromWorklist = (bioentityIds: Array<string>): void => {
    let dataTable = this.props.dataTable;
    for (const id of bioentityIds) {
      dataTable = DataTableUtil.addRowToStandaloneSampleTable(dataTable, {fileId: id});
    }
    this.props.onUpdate(dataTable);
  };

  override render(): JSX.Element {
    const {children, dataTable, fileSummariesById} = this.props;
    const rowLabels = DataTableUtil.getRowLabelsForFileDataTable(dataTable);
    const table = DataTableUtil.convertDataTableToTable(this.props.dataTable, rowLabels);
    const sampleIds = DataTableUtil.getSampleIdsForFileDataTable(this.props.dataTable, fileSummariesById);
    return (
      <>
        {this.state.openModal === AddSamplesModalType.EXTERNAL && (
          <ModalAnchor open={true}>
            <StandaloneDataTableAddSampleModal
              linkType={FIELD_TYPES.BIOENTITY_LINK.type}
              targetSchemaId={getTargetSchemaIdForFileLinkContext(this.props.dataTable)}
              onRequestClose={this.handleCloseModal}
              onRequestSubmit={this.handleAddFile}
            />
          </ModalAnchor>
        )}
        {this.state.openModal === AddSamplesModalType.INTERNAL && (
          <LegacySamplesSourceCollector
            note={this.props.note}
            entryId={this.props.entryId}
            limitSamplesTo={this.sampleFilter}
          >
            {(sampleSources) => (
              <ModalAnchor open={true}>
                <AddStandaloneSamplesModal
                  onRequestClose={this.handleCloseModal}
                  onSamplesSelected={this.handleAddSamples}
                  sources={sampleSources}
                />
              </ModalAnchor>
            )}
          </LegacySamplesSourceCollector>
        )}
        {children({
          table,
          sampleIds,
          addMenuItems: _.compact([
            {
              name: 'Add row',
              icon: 'compose',
              handleOnClick: this.handleAddExternalSampleClick,
              dataTestAttrs: {component: 'DataTableFileLinkContext', element: 'addRowMenuItem'},
            },
            {
              name: 'Add samples',
              icon: 'compose',
              disabled: false,
              handleOnClick: this.handleAddInternalSampleClick,
              dataTestAttrs: {component: 'DataTableFileLinkContext', element: 'addSamplesMenuItem'},
            },
            BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS
              ? {
                  name: 'Add from worklist',
                  icon: ModelTypeConstants.WORKLIST,
                  handleOnClick: async () => {
                    const worklist = await fillFromWorklist(
                      [WorklistType.BIOENTITY],
                      WORKLIST_FILL_LOCATION.STANDALONE_BIOENTITY_RESULTS_TABLE
                    );
                    if (worklist) {
                      this.handleAddSamplesFromWorklist(worklist.itemIds);
                    }
                  },
                  dataTestAttrs: {component: 'DataTableFileLinkContext', element: 'addFromWorklistMenuItem'},
                }
              : null,
          ]),
        })}
      </>
    );
  }
}

export default connect(
  null,
  mapDispatchToProps
)(DataTableFileLinkContextConnector.connect(DataTableFileLinkContext));
